import { Box, useTheme } from '@mui/material'
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip
} from 'recharts'
import { format, subDays, addDays } from 'date-fns'
import { EarningsSummaryDTO } from '../types/EarningsSummary'
import formatCurrency from '../../../utils/formatCurrency'

type Props = {
  summaries: EarningsSummaryDTO[] | undefined
  startDate?: Date
  endDate?: Date
}

const aggregateEarningsByDay = (
  summaries: EarningsSummaryDTO[],
  startDate: Date,
  endDate: Date
): { [key: string]: number } =>
  summaries.reduce(
    (acc, curr) => {
      const day = format(new Date(curr.date), 'yyyy-MM-dd')
      const currDate = new Date(day)
      if (currDate >= startDate && currDate <= endDate) {
        if (!acc[day]) {
          acc[day] = 0
        }
        acc[day] += curr.netEarningsAmount
      }
      return acc
    },
    {} as { [key: string]: number }
  )

const getRevenuePerDay = (
  summaries: EarningsSummaryDTO[],
  minDate: Date,
  maxDate: Date
) => {
  const revenue = aggregateEarningsByDay(summaries, minDate, maxDate)
  const daysInRange = Array.from(
    {
      length:
        (maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24) + 1
    },
    (_, i) => subDays(maxDate, i)
  )
  return daysInRange.map((date) => ({
    date: date.getTime(),
    total: revenue[format(date, 'yyyy-MM-dd')] || 0
  }))
}

const EarningsChart = ({ summaries, startDate, endDate }: Props) => {
  const theme = useTheme()

  if (!summaries?.length) return <></>

  const invalidDates =
    (startDate ? isNaN(startDate.getTime()) : true) ||
    (endDate ? isNaN(endDate.getTime()) : true)

  if (invalidDates) {
    startDate = new Date(summaries[0].date)
    endDate = new Date(summaries[summaries.length - 1].date)
  }

  if (!startDate || !endDate) return <></>

  return (
    <Box sx={{ width: 'inherit', padding: '10px', height: 300 }}>
      <ResponsiveContainer>
        <AreaChart data={getRevenuePerDay(summaries, startDate, endDate)}>
          <defs>
            <linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="5%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0.8}
              />
              <stop
                offset="95%"
                stopColor={theme.palette.primary.main}
                stopOpacity={0}
              />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            name="Date"
            type="number"
            scale="time"
            domain={[addDays(startDate, 1).getTime(), endDate.getTime()]}
            tickFormatter={(v) => new Date(v).toLocaleDateString()}
          />
          <YAxis dataKey="total" name="Revenue" unit="$" />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            cursor={{ strokeDasharray: '3 3' }}
            formatter={formatCurrency}
            labelFormatter={(v) => new Date(v).toLocaleDateString()}
            labelStyle={{ color: theme.palette.primary.main }}
          />
          <Area
            type="monotone"
            dataKey="total"
            stroke={theme.palette.primary.main}
            strokeWidth={2}
            fill="url(#colorUv)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  )
}

export default EarningsChart
